import { track } from '@vercel/analytics';

/**
 * `track` that cannot break the flow it is measuring.
 *
 * Vercel Analytics is a third-party script: an ad blocker, a CSP, an offline
 * tab or an SDK error can make the call throw. Every call site in the two
 * conversion forms sits on the submit path, so an unguarded throw turns a
 * measurement into an outage - the handler unwinds mid-submit, the button stays
 * disabled on "Sending...", no banner renders, and the visitor is left with a
 * form that silently did nothing. Losing one analytics event is always the
 * cheaper failure.
 */
export function safeTrack(event: string): void {
  try {
    track(event);
  } catch (error) {
    console.error('[analytics] track failed:', event, error);
  }
}

/** Events already sent by {@link trackOnce} in this page session. */
const sentOnce = new Set<string>();

/**
 * `safeTrack` for an event that reports a per-BUILD or per-SESSION fact rather
 * than a per-interaction one.
 *
 * Some degraded-mode signals (`newsletter_endpoint_absent`) are raised from a
 * component that mounts on every blog post page and again on every modal open,
 * and they report a condition that is constant for the whole build. Firing per
 * mount would spend metered analytics volume repeating a value that carries no
 * information after its first occurrence - and an operator reading the
 * dashboard cannot distinguish "one broken build" from "many broken builds"
 * once the count tracks pageviews instead of deploys.
 */
export function trackOnce(event: string): void {
  if (sentOnce.has(event)) return;
  sentOnce.add(event);
  safeTrack(event);
}

/**
 * Test-only. The `sentOnce` set is module state, so it outlives a single case
 * within a file and would make the second case asserting a once-per-session
 * event fail for a reason that has nothing to do with the component.
 */
export function clearTrackOnceHistory(): void {
  sentOnce.clear();
}
